import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, firestore } from '../../firebase/config';
import { 
  doc, 
  getDoc, 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy, 
  limit 
} from 'firebase/firestore';

const BusinessDashboardHome = () => {
  const [user, setUser] = useState(null);
  const [businessProfile, setBusinessProfile] = useState(null);
  const [showProfileIncompleteModal, setShowProfileIncompleteModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    activeJobs: 0,
    newApplications: 0,
    totalApplications: 0,
    unreadMessages: 0,
    profileViews: 0,
    hiredCandidates: 0
  });
  const [recentJobs, setRecentJobs] = useState([]);
  const [recentApplications, setRecentApplications] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await loadDashboardData(currentUser);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const loadDashboardData = async (currentUser) => {
    try {
      // Get user data
      const userDoc = await getDoc(doc(firestore, 'users', currentUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        // Check if business profile is complete
        if (!userData.isBusinessProfileComplete) {
          setShowProfileIncompleteModal(true);
        }
        
        // Get business profile if exists
        if (userData.businessId) {
          const businessDoc = await getDoc(doc(firestore, 'businesses', userData.businessId));
          if (businessDoc.exists()) {
            setBusinessProfile(businessDoc.data());
          }
          
          // Load business statistics
          await loadBusinessStats(userData.businessId);
          await loadRecentData(userData.businessId);
        }
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    }
  };

  const loadBusinessStats = async (businessId) => {
    try {
      // Get active jobs count
      const jobsQuery = query(
        collection(firestore, 'jobs'),
        where('businessId', '==', businessId),
        where('isActive', '!=', false)
      );
      const jobsSnapshot = await getDocs(jobsQuery);
      const activeJobs = jobsSnapshot.size;

      // Get total applications
      const applicationsQuery = query(
        collection(firestore, 'jobApplications'),
        where('businessId', '==', businessId)
      );
      const applicationsSnapshot = await getDocs(applicationsQuery);
      const totalApplications = applicationsSnapshot.size;

      // Get new applications (last 7 days)
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      
      const newApplicationsQuery = query(
        collection(firestore, 'jobApplications'),
        where('businessId', '==', businessId),
        where('appliedAt', '>=', sevenDaysAgo)
      );
      const newApplicationsSnapshot = await getDocs(newApplicationsQuery);
      const newApplications = newApplicationsSnapshot.size;

      // Get hired candidates
      const hiredQuery = query(
        collection(firestore, 'jobApplications'),
        where('businessId', '==', businessId),
        where('status', '==', 'hired')
      );
      const hiredSnapshot = await getDocs(hiredQuery);
      const hiredCandidates = hiredSnapshot.size;

      setStats({
        activeJobs,
        newApplications,
        totalApplications,
        unreadMessages: 0, // TODO: Implement chat unread count
        profileViews: Math.floor(Math.random() * 100) + 20, // Mock data for now
        hiredCandidates
      });
    } catch (error) {
      console.error('Error loading business stats:', error);
    }
  };

  const loadRecentData = async (businessId) => {
    try {
      // Load recent jobs
      const recentJobsQuery = query(
        collection(firestore, 'jobs'),
        where('businessId', '==', businessId),
        orderBy('createdAt', 'desc'),
        limit(5)
      );
      const recentJobsSnapshot = await getDocs(recentJobsQuery);
      const jobs = recentJobsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setRecentJobs(jobs);

      // Load recent applications
      const recentApplicationsQuery = query(
        collection(firestore, 'jobApplications'),
        where('businessId', '==', businessId),
        orderBy('appliedAt', 'desc'),
        limit(5)
      );
      const recentApplicationsSnapshot = await getDocs(recentApplicationsQuery);
      const applications = recentApplicationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Fetch user profiles for applicants
      const userProfiles = {};
      for (const app of applications) {
        if (app.userId && !userProfiles[app.userId]) {
          try {
            const userDoc = await getDoc(doc(firestore, 'userProfiles', app.userId));
            if (userDoc.exists()) {
              userProfiles[app.userId] = userDoc.data();
            } else {
              const userData = await getDoc(doc(firestore, 'users', app.userId));
              if (userData.exists()) {
                const data = userData.data();
                userProfiles[app.userId] = {
                  firstName: data.firstName || '',
                  lastName: data.lastName || ''
                };
              }
            }
          } catch (error) {
            console.error('Error fetching user profile:', error);
          }
        }
      }

      // Add user names to applications
      const applicationsWithNames = applications.map(app => ({
        ...app,
        applicantName: userProfiles[app.userId] 
          ? `${userProfiles[app.userId].firstName} ${userProfiles[app.userId].lastName}`.trim()
          : 'Unknown Applicant'
      }));

      setRecentApplications(applicationsWithNames);
    } catch (error) {
      console.error('Error loading recent data:', error);
    }
  };

  const getJobTitle = (jobId, jobsList) => {
    const job = jobsList.find(j => j.id === jobId);
    return job?.title || 'Unknown Position';
  };

  const handleCompleteProfile = () => {
    navigate('/business-dashboard/business-profile');
    setShowProfileIncompleteModal(false);
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      const diffInMinutes = Math.floor((now - date) / (1000 * 60));
      return diffInMinutes < 1 ? 'just now' : `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return diffInDays === 1 ? '1 day ago' : `${diffInDays} days ago`;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Profile Incomplete Modal */}
      {showProfileIncompleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Complete Your Business Profile</h3>
            <p className="text-gray-600 mb-6">
              To start posting jobs and accessing all features, please complete your business profile.
            </p>
            <div className="flex space-x-4">
              <button
                onClick={() => setShowProfileIncompleteModal(false)}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-md text-gray-700 hover:bg-slate-50"
              >
                Later
              </button>
              <button
                onClick={handleCompleteProfile}
                className="flex-1 py-2 px-4 bg-orange-600 text-white rounded-md hover:bg-orange-700"
              >
                Complete Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dashboard Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600">
          Welcome back{businessProfile?.name ? `, ${businessProfile.name}` : ''}! Here's what's happening with your business.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 00-2 2H8a2 2 0 00-2-2V6m8 0h2a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2h2" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Jobs</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.activeJobs}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">New Applications</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.newApplications}</p>
              <p className="text-xs text-gray-500">Last 7 days</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <svg className="h-6 w-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Applications</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.totalApplications}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <svg className="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Hired Candidates</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.hiredCandidates}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="space-y-8">
        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <button
              onClick={() => navigate('/business-dashboard/jobs')}
              className="p-4 border border-gray-200 rounded-lg hover:bg-slate-50 text-left block w-full"
            >
              <div className="flex items-center">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <svg className="h-5 w-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="font-medium text-gray-900">Post New Job</p>
                  <p className="text-sm text-gray-500">Create a new job listing</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => navigate('/business-dashboard/applicants')}
              className="p-4 border border-gray-200 rounded-lg hover:bg-slate-50 text-left block w-full"
            >
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="font-medium text-gray-900">Review Applicants</p>
                  <p className="text-sm text-gray-500">Check new applications</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => navigate('/business-dashboard/chat')}
              className="p-4 border border-gray-200 rounded-lg hover:bg-slate-50 text-left block w-full"
            >
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="font-medium text-gray-900">Messages</p>
                  <p className="text-sm text-gray-500">Chat with applicants</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => navigate('/business-dashboard/business-profile')}
              className="p-4 border border-gray-200 rounded-lg hover:bg-slate-50 text-left block w-full"
            >
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <svg className="h-5 w-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h3M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="font-medium text-gray-900">Business Setup</p>
                  <p className="text-sm text-gray-500">Configure business details</p>
                </div>
              </div>
            </button>
          </div>
        </div>

        {/* Recent Applications */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-medium text-gray-900">Recent Applications</h2>
            <button
              onClick={() => navigate('/business-dashboard/applicants')}
              className="text-[#FF8C00] hover:text-[#FF8C00]/80 text-sm font-medium"
            >
              View All
            </button>
          </div>
          {recentApplications.length === 0 ? (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No applications yet</h3>
              <p className="mt-1 text-sm text-gray-500">Start by posting your first job to receive applications.</p>
              <div className="mt-6">
                <button
                  onClick={() => navigate('/business-dashboard/jobs')}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-[#FF8C00] hover:bg-[#FF8C00]/80"
                >
                  <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Post Your First Job
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {recentApplications.slice(0, 5).map((application) => (
                <div key={application.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold">
                      {application.applicantName.charAt(0)}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{application.applicantName}</p>
                      <p className="text-sm text-gray-500">
                        Applied for: {getJobTitle(application.jobId, recentJobs)}
                      </p>
                      <p className="text-xs text-gray-400">{formatTimeAgo(application.appliedAt)}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                      application.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      application.status === 'hired' ? 'bg-green-100 text-green-800' :
                      application.status === 'reviewed' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {application.status || 'Pending'}
                    </span>
                    <button
                      onClick={() => navigate('/business-dashboard/applicants')}
                      className="text-[#FF8C00] hover:text-[#FF8C00]/80 text-sm font-medium"
                    >
                      Review
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BusinessDashboardHome; 